import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useAppConfig } from "@/appConfig";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { pageURLs } from "@/constants";
import GitchaShareAccess from "@/components/GitchaShareAccess";
import ShareYourAdModal from "@/components/MemberWantAd/v2/ShareYourAdModal";
import { shareWantAdSocial, syndicateWantAdListing } from "@/services/v2/wantAdService";

const WantListingShare = ({ adDetails, slug, onHide, onSyndicated, audience = "members" }) => {
  const { flags } = useAppConfig() || {};
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const queryClient = useQueryClient();
  const isBlsTenant = !!flags?.mlsAgentEditor && !isGitchaDefaultWebsite;
  const wantAdId = adDetails?.wantAdId;
  const [syndicated, setSyndicated] = React.useState(!!adDetails?.syndicateToThePublic);

  const links = useQuery({
    queryKey: ["wantAdShareLinks", wantAdId],
    queryFn: async () => {
      const response = await shareWantAdSocial({ wantAdId, platform: "facebook" });
      return {
        shareUrl: response?.result?.shareUrl || "",
        fbUrl: response?.result?.facebookShareUrl || "",
        imageUrl: response?.result?.imageUrl || "",
      };
    },
    enabled: isBlsTenant && !!wantAdId,
  });

  const syndicate = useMutation({
    mutationFn: () => syndicateWantAdListing(wantAdId),
    onSuccess: (response) => {
      if (response?.errorCode === 0 && response?.result) {
        setSyndicated(true);
        queryClient.resetQueries({ queryKey: ["wantAdShareLinks", wantAdId] });
        queryClient.invalidateQueries({ queryKey: ["onboardingProgress"] });
        if (onSyndicated) onSyndicated(wantAdId);
        return;
      }
      toast.error("Could not syndicate this Want-Listing. Please try again.");
    },
    onError: () => toast.error("Could not syndicate this Want-Listing. Please try again."),
  });

  if (!isBlsTenant) {
    return <ShareYourAdModal show onHide={onHide} adDetails={adDetails} isWantAdCreated={false} />;
  }

  return (
    <GitchaShareAccess
      show
      onHide={onHide}
      initialAudience={audience}
      isSyndicated={syndicated}
      memberUrl={`${window.location.origin}${pageURLs.wantAdDetails}/${slug || wantAdId}`}
      wantAdId={wantAdId}
      adDetails={adDetails}
      shareUrl={links.data?.shareUrl || ""}
      fbUrl={links.data?.fbUrl || ""}
      imageUrl={links.data?.imageUrl || ""}
      isSyndicating={syndicate.isPending}
      onSyndicate={() => syndicate.mutate()}
    />
  );
};

export default WantListingShare;
